import { ace_index } from '@axo/shared/data-access/types';
import { ReactNode } from 'react';
import { ApplicationLink } from '../ApplicationLink';
import { StatusTag } from '../StatusTag';
import { formatDatetime } from '../../utils/formatDate';
import { UserName } from '../UserName';
import { BrokerName } from '../BrokerName';
import { LenderName } from './LenderName';
import { ApprovedLenderNames } from './ApprovedLenderNames';
import { PartnerName } from './PartnerName';
import { QuoteStatuses } from './QuoteStatuses';
import { RequestStatuses } from './RequestStatuses';

const tables = ace_index.SortByTable;

/**
 * Column represents a column in a search result table.
 */
interface Column {
  /**
   * label determines how the column will be presented in a list
   * or in a table header. This is the visible name of the column.
   */
  readonly label: string;

  /**
   * render should render the appropriate field from a search result.
   */
  readonly render: (app: ace_index.LoanApplication) => ReactNode;

  /**
   * sort determines how sorting this column will affect the search
   * query. If sort is undefined, the column will not be sortable.
   */
  readonly sort?: ace_index.SortBy | ace_index.SortBy[];

  /**
   * mandatory flags wheter or not this column can be shown/hidden.
   * Setting mandatory to {true} means that it will always be visible.
   */
  readonly mandatory?: boolean;
}

/** COLUMNS is the configuration of available search result columns. */
const COLUMNS = {
  HumanReadableID: {
    label: 'Application Number',
    mandatory: true,
    render: (app) => (
      <ApplicationLink
        id={app.ID}
        humanReadableID={app.HumanReadableID}
        marketCountry={app.MarketCountryCode}
      />
    ),
    sort: {
      Table: tables.LoanApplication,
      Column: 'HumanReadableID',
    },
  },

  CompletedAt: {
    label: 'Application Completed',
    render: (app) => formatDatetime(app.CompletedAt),
    sort: {
      Table: tables.LoanApplication,
      Column: 'CompletedAt',
    },
  },

  Status: {
    label: 'Status',
    render: (app) => {
      const status = app.Status?.Name?.toString() ?? '';
      return <StatusTag status={status}>{status}</StatusTag>;
    },
    sort: {
      Table: tables.LoanApplication,
      Column: 'StatusCode',
    },
  },

  AppliedAmount: {
    label: 'Loan Amount (requested)',
    render: (app) => app.Amount,
    sort: {
      Table: tables.LoanApplication,
      Column: 'Amount',
    },
  },

  GrossIncomeYearly: {
    label: 'Yearly Salary',
    render: (app) =>
      app.GrossIncomeYearly != null ? app.GrossIncomeYearly.toLocaleString() : '-',
  },

  FirstName: {
    label: 'First Name',
    render: (app) => app.Person?.FirstName,
    sort: {
      Table: tables.Person,
      Column: 'FirstName',
    },
  },

  LastName: {
    label: 'Last Name',
    render: (app) => app.Person?.LastName,
    sort: {
      Table: tables.Person,
      Column: 'LastName',
    },
  },

  UpdatedAt: {
    label: 'Last application update',
    render: (app) => formatDatetime(app.UpdatedAt),
    sort: {
      Table: tables.LoanApplication,
      Column: 'UpdatedAt',
    },
  },

  LastUpdateAt: {
    label: 'Last application/quote update',
    render: (app) => formatDatetime(app.LoanApplicationSummary?.LastUpdateAt),
    sort: {
      Table: tables.LoanApplication,
      Column: 'LastUpdateAt',
    },
  },

  Owner: {
    label: 'Owner',
    render: (app) => <UserName clientID={app.Assignment?.UserID ?? ''} />,
    sort: {
      Table: tables.Assignment,
      Column: 'UserID',
    },
  },

  LockedBy: {
    label: 'Locked by',
    render: (app) => <UserName clientID={app.Lock?.LockedByID ?? ''} />,
    sort: {
      Table: tables.Lock,
      Column: 'LockedByID',
    },
  },

  QuoteStatuses: {
    label: 'Quote Statuses',
    render: (app) => <QuoteStatuses app={app} />,
  },

  RequestStatuses: {
    label: 'Request Statuses',
    render: (app) => <RequestStatuses app={app} />,
  },

  LastMyPageLogin: {
    label: 'Last MyPage Login at',
    render: (app) =>
      formatDatetime(app.LoanApplicationSummary?.LastMyPageLoginAt),
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastMyPageLoginAt',
    },
  },

  LastAcceptBy: {
    label: 'Last Accept by',
    render: (app) => {
      switch (app.LoanApplicationSummary?.LastAcceptByCode) {
        case ace_index.AcceptedByCode.MyPage:
          return 'Customer (MyPage)';
        case ace_index.AcceptedByCode.AutoAccept:
          return 'AutoAccept';
        case ace_index.AcceptedByCode.ACE:
          return 'ACE';
        case undefined:
          return '';
        default:
          return 'Unknown source';
      }
    },
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastAcceptByCode',
    },
  },

  NumCalls: {
    label: '# Calls',
    render: (app) => app.LoanApplicationSummary?.NumCalls,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'NumCalls',
    },
  },

  NumAnsweredCalls: {
    label: '# Answered',
    render: (app) => app.LoanApplicationSummary?.NumAnsweredCalls,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'NumAnsweredCalls',
    },
  },

  NumNotAnsweredCalls: {
    label: '# Unanswered',
    render: (app) => app.LoanApplicationSummary?.NumNotAnsweredCalls,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'NumNotAnsweredCalls',
    },
  },

  LastCallAt: {
    label: 'Last Call Attempt',
    render: (app) => formatDatetime(app.LoanApplicationSummary?.LastCallAt),
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastCallAt',
    },
  },

  LastAcceptedLenderID: {
    label: 'Accepted bank (latest accepted)',
    render: (app) => {
      const id = app.LoanApplicationSummary?.LastAcceptedLenderID;

      if (id === undefined) {
        return null;
      }

      return <LenderName marketCountry={app.MarketCountryCode} lenderID={id} />;
    },
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastAcceptedLenderID',
    },
  },

  ApprovedByLenderIDs: {
    label: 'Approved by',
    render: (app) => <ApprovedLenderNames app={app} />,
  },

  LastAcceptedAmount: {
    label: 'Accepted Amount',
    render: (app) => app.LoanApplicationSummary?.LastAcceptedAmount,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastAcceptedAmount',
    },
  },

  HighestApprovedAmount: {
    label: 'Highest Approved Amount',
    render: (app) => app.LoanApplicationSummary?.HighestApprovedAmount,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'HighestApprovedAmount',
    },
  },

  FirstApprovedOfferAt: {
    label: 'Approved since',
    render: (app) =>
      formatDatetime(app.LoanApplicationSummary?.FirstApprovedOfferAt),
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'FirstApprovedOfferAt',
    },
  },

  FirstAcceptedOfferAt: {
    label: 'Accepted since',
    render: (app) =>
      formatDatetime(app.LoanApplicationSummary?.FirstAcceptedOfferAt),
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'FirstAcceptedOfferAt',
    },
  },

  LastAcceptedOfferAt: {
    label: 'Accepted at (latest offer accepted)',
    render: (app) =>
      formatDatetime(app.LoanApplicationSummary?.LastAcceptedOfferAt),
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastAcceptedOfferAt',
    },
  },

  LastDisbursedQuoteAt: {
    label: 'Disbursed At',
    render: (app) =>
      formatDatetime(app.LoanApplicationSummary?.LastDisbursedQuoteAt),
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastDisbursedQuoteAt',
    },
  },

  LastLoanRequestAmount: {
    label: 'Applied amount (from latest loan request)',
    render: (app) => app.LoanApplicationSummary?.LastLoanRequestAmount,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastLoanRequestAmount',
    },
  },

  NumRegisteredLoanRequests: {
    label: 'No. sent requests',
    render: (app) => app.LoanApplicationSummary?.NumRegisteredLoanRequests,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'NumRegisteredLoanRequests',
    },
  },

  NumAnsweredLoanRequests: {
    label: 'No. answered requests',
    render: (app) => app.LoanApplicationSummary?.NumAnsweredLoanRequests,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'NumAnsweredLoanRequests',
    },
  },

  Phone: {
    label: 'Phone',
    render: (app) => app.Customer?.MobilePhoneNumber,
    sort: {
      Table: tables.Customer,
      Column: 'MobilePhoneNumber',
    },
  },

  Email: {
    label: 'Email',
    render: (app) => app.Customer?.Email,
    sort: {
      Table: tables.Customer,
      Column: 'Email',
    },
  },

  SSN: {
    label: 'SSN',
    render: (app) => app.Person?.SocialSecurityNumber,
    sort: {
      Table: tables.Person,
      Column: 'SocialSecurityNumber',
    },
  },

  ApplicantRole: {
    label: 'Applicant Role',
    render: (app) =>
      app.Channels?.find((x) => !!x.InternalSource)?.InternalIdentityRoleName
        ?.Name,
  },

  NumApprovedQuotes: {
    label: 'No. Approved Quotes',
    render: (app) => app.LoanApplicationSummary?.NumApprovedQuotes,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'NumApprovedQuotes',
    },
  },

  Product: {
    label: 'Product',
    render: (app) => app.Product?.Name,
    sort: {
      Table: tables.LoanApplication,
      Column: 'ProductCode',
    },
  },

  NeedsFollowupAt: {
    label: 'Needs followup at',
    render: (app) =>
      formatDatetime(app.LoanApplicationSummary?.NeedsFollowupAt),
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'NeedsFollowupAt',
    },
  },

  UserRequestedFollowupAt: {
    label: 'Follow-Up by User',
    render: (app) =>
      formatDatetime(app.LoanApplicationSummary?.UserRequestedFollowupAt),
  },

  FollowupComment: {
    label: 'Followup comment',
    render: (app) => app.LoanApplicationSummary?.FollowupComment,
  },

  Broker: {
    label: 'Broker',
    render: (app) => <BrokerName id={app?.Broker} />,
    sort: {
      Table: tables.LoanApplication,
      Column: 'Broker',
    },
  },

  Partner: {
    label: 'Partner',
    render: (app) => <PartnerName summary={app.LoanApplicationSummary} />,
    sort: [
      { Table: tables.LoanApplicationSummary, Column: 'CampaignContent' },
      { Table: tables.LoanApplicationSummary, Column: 'CampaignMedium' },
      { Table: tables.LoanApplicationSummary, Column: 'CampaignName' },
    ],
  },

  LastCompletedStep: {
    label: 'Last completed step',
    render: (app) => app.LoanApplicationSummary?.LastCompletedStep,
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastCompletedStep',
    },
  },

  LastSuccessfulAuthAttempt: {
    label: 'Last successful auth attempt',
    render: (app) =>
      formatDatetime(app.LoanApplicationSummary?.LastSuccessfulAuthAttempt),
    sort: {
      Table: tables.LoanApplicationSummary,
      Column: 'LastSuccessfulAuthAttempt',
    },
  },
} satisfies { [key: string]: Column };

/** ColumnID is a generated type with IDs of available result columns. */
export type ColumnID = keyof typeof COLUMNS;

/** COLUMN_IDS is a generated list of result column IDs. */
export const COLUMN_IDS = [...Object.keys(COLUMNS)] as ColumnID[];

/** column is a utility for looking up details for a specific result column. */
export function column(id: ColumnID | '' | undefined): Column | undefined {
  if (id === undefined || id === '') {
    return undefined;
  }
  return COLUMNS[id];
}
